import { pxToEm } from 'util/pxToEm';
import { Box, SvgIcon, Typography } from '@mui/material';
import React, { ReactElement, useState } from 'react';

export interface StarRatingProps {
  value: number;
  max?: number;
  onChange?: (value: number) => void;
  size?: number;
  readOnly?: boolean;
  labels?: Record<number, string>;
  showLabels?: boolean;
}

const StarIcon = ({ filled }: { filled: boolean }) => (
  <SvgIcon
    sx={{
      fontSize: '1em',
      color: filled ? '#FFD700' : '#E0E0E0',
    }}
    viewBox="0 0 48 44"
  >
    <path
      d="M23.7714 35.3325L38.4571 43.9634L34.5714 27.6982L47.5451 16.7542L30.4594 15.3439L23.7714 0.00222778L17.0857 15.3417L0 16.7542L12.9737 27.6982L9.088 43.9634L23.7714 35.3325Z"
      fill="currentColor"
    />
  </SvgIcon>
);

export const StarRating = ({
  value,
  max = 5,
  onChange,
  size = 30,
  readOnly = false,
  labels,
  showLabels = false,
}: StarRatingProps): ReactElement => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleClick = (index: number) => {
    if (!readOnly && onChange) onChange(index + 1);
  };

  const handleMouseEnter = (index: number) => {
    if (!readOnly) setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  return (
    <Box display="flex" justifyContent="space-start" alignItems="flex-start">
      {Array.from({ length: max }, (_, index) => {
        const starValue = index + 1;
        const isFilled = index < value;
        const label = labels?.[starValue];
        const shouldShowLabel =
          showLabels &&
          label &&
          (value > 0 || hoveredIndex !== null) &&
          (index === hoveredIndex || (value > 0 && index === value - 1));

        return (
          <Box
            key={index}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={13}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            sx={{
              position: 'relative',
              minWidth: showLabels ? pxToEm(80) : 'auto',
            }}
          >
            <Box
              onClick={() => handleClick(index)}
              sx={{
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: pxToEm(size),
                lineHeight: 0,
                transition: 'transform 0.1s',
                '&:hover': {
                  transform: readOnly ? undefined : 'scale(1.1)',
                },
              }}
            >
              <StarIcon filled={isFilled} />
            </Box>
            {showLabels && (
              <Box
                sx={{
                  minHeight: pxToEm(16),
                  minWidth: pxToEm(80),
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {shouldShowLabel && (
                  <Typography
                    sx={{
                      fontSize: pxToEm(14),
                      color: '#383838',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }}
                  >
                    {label}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

